import React, { useState } from "react"
import { Link } from "gatsby"
import Layout from "../../components/Layout"
import books from "../../data/books.json"
import recommendations from "../../data/book-recommendations.json"
import {
  BooksPage,
  Intro,
  ShelfNav,
  Shelf,
  BookGrid,
  Book,
  Cover,
  Empty
} from "./style"

const shelves = [
  {
    key: "currentlyReading",
    id: "currently-reading",
    title: "Currently reading",
    caption: "On my bedside table."
  },
  {
    key: "recommended",
    id: "i-recommend",
    title: "I recommend",
    caption: "Books I’d put in your hands."
  },
  {
    key: "read",
    id: "read",
    title: "Read recently",
    caption: "The books that have kept me company."
  }
]

const library = new Map(
  [...books.currentlyReading, ...books.read].map(book => [book.url, book])
)
const shelfBooks = {
  ...books,
  recommended: [...new Set(recommendations.selectedBooks || [])]
    .map(url => library.get(url))
    .filter(Boolean)
}

const BookCover = ({ book }) => {
  const [failed, setFailed] = useState(false)
  return (
    <Cover>
      {book.coverUrl && !failed ? (
        <img
          src={book.coverUrl}
          alt=""
          loading="lazy"
          width="160"
          height="240"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="cover-type" aria-hidden="true">
          <span>{book.title}</span>
          <small>{book.author}</small>
        </span>
      )}
    </Cover>
  )
}

const BookList = ({ items }) => (
  <BookGrid>
    {items.map(book => (
      <Book key={book.url || `${book.title}-${book.author}`}>
        <a
          href={book.url || books.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BookCover book={book} />
          <h3>{book.title}</h3>
          <p>{book.author}</p>
        </a>
        {book.rating != null && (
          <small aria-label={`Rated ${book.rating} out of 5 stars`}>
            <span aria-hidden="true">★</span> {book.rating} / 5
          </small>
        )}
      </Book>
    ))}
  </BookGrid>
)

const Books = ({ location }) => (
  <Layout
    location={location}
    seoTitle="Books"
    description="What I'm reading, what I've read recently, and the books I recommend."
  >
    <BooksPage>
      <Intro>
        <span className="eyebrow">A personal library</span>
        <h1>Between the pages.</h1>
        <p>
          What I’m reading, what I’ve read recently, and the books I recommend.
        </p>
        <div className="source">
          {books.profileUrl && (
            <a
              href={books.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Follow along on StoryGraph <span aria-hidden="true">↗</span>
            </a>
          )}
          <Link to="/category/book-notes/">
            My book notes <span aria-hidden="true">↗</span>
          </Link>
        </div>
        {books.updatedAt && (
          <small>
            Last updated{" "}
            {new Date(`${books.updatedAt}T12:00:00Z`).toLocaleDateString(
              "en-CA",
              {
                month: "long",
                day: "numeric",
                year: "numeric",
                timeZone: "UTC"
              }
            )}
          </small>
        )}
      </Intro>
      <ShelfNav aria-label="Bookshelves">
        {shelves.map(shelf => (
          <a key={shelf.id} href={`#${shelf.id}`}>
            {shelf.title}
            <span>{shelfBooks[shelf.key].length}</span>
          </a>
        ))}
      </ShelfNav>
      {shelves.map((shelf, index) => (
        <Shelf
          id={shelf.id}
          key={shelf.id}
          aria-labelledby={`${shelf.id}-title`}
        >
          <header>
            <span className="number">0{index + 1}</span>
            <div>
              <h2 id={`${shelf.id}-title`}>{shelf.title}</h2>
              <p>{shelf.caption}</p>
              {shelf.key === "read" &&
                books.readEntryCount > books.read.length && (
                  <p className="read-note">
                    {books.readEntryCount} reading entries on StoryGraph ·
                    repeated editions shown once.
                  </p>
                )}
            </div>
            <span className="count">
              {shelfBooks[shelf.key].length}{" "}
              {shelfBooks[shelf.key].length === 1 ? "book" : "books"}
            </span>
          </header>
          {shelfBooks[shelf.key].length ? (
            <>
              <BookList items={shelfBooks[shelf.key].slice(0, 24)} />
              {shelfBooks[shelf.key].length > 24 && (
                <details>
                  <summary>
                    Browse the remaining {shelfBooks[shelf.key].length - 24}{" "}
                    books
                  </summary>
                  <BookList items={shelfBooks[shelf.key].slice(24)} />
                </details>
              )}
            </>
          ) : (
            <Empty>
              {shelf.key === "recommended"
                ? "Recommendations coming soon."
                : books.updatedAt
                ? "Nothing on this shelf just yet."
                : "This shelf is waiting for its first StoryGraph import."}
            </Empty>
          )}
        </Shelf>
      ))}
    </BooksPage>
  </Layout>
)

export default Books
